import { useTheme } from "@emotion/react";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { MdClose, MdOutlinePictureAsPdf, MdOutlineUploadFile } from "react-icons/md";

export type AttachmentEntry = {
  fileName: string;
  fileType: string;
  contentBase64: string;
};

type Props = {
  attachments: AttachmentEntry[];
  onChange: (attachments: AttachmentEntry[]) => void;
};

const readFileAsBase64 = (file: File): Promise<AttachmentEntry> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result;
      if (typeof dataUrl !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      const base64 = dataUrl.split(",")[1] ?? "";
      resolve({ fileName: file.name, fileType: file.type, contentBase64: base64 });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

export const SupportAttachmentPicker: React.FC<Props> = ({ attachments, onChange }) => {
  const theme = useTheme();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const results = await Promise.allSettled(acceptedFiles.map(readFileAsBase64));
      const entries = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
      onChange([...attachments, ...entries]);
    },
    [attachments, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*,application/pdf",
    multiple: true,
  });

  const removeAttachment = (index: number) => {
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", width: "100%" }}>
      <div
        css={{
          border: isDragActive ? `2px dashed ${theme.colors.primary}` : `2px dashed ${theme.colors.border}`,
          borderRadius: "8px",
          padding: "10px",
          textAlign: "center",
          transition: "border-color 0.15s",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          {...getRootProps()}
          css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.5rem",
            padding: "1rem",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <MdOutlineUploadFile size={40} css={{ color: isDragActive ? theme.colors.primary : theme.colors.primaryLight }} />
          <span
            css={{
              fontFamily: theme.displayFontFamily,
              fontSize: "14px",
              color: isDragActive ? theme.colors.primary : theme.colors.primary,
            }}
          >
            Przeciągnij i upuść lub <strong>dodaj załączniki</strong>
          </span>
        </div>
      </div>
      {attachments.length > 0 && (
        <div css={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {attachments.map((att, i) => {
            const isImage = att.fileType.startsWith("image/");
            const isPdf = att.fileType === "application/pdf";
            const dataUrl = `data:${att.fileType};base64,${att.contentBase64}`;
            return (
              <div
                key={`${att.fileName}-${i}`}
                css={{
                  position: "relative",
                  width: "7rem",
                  height: "7rem",
                  borderRadius: theme.smallBorderRadius,
                  border: `1px solid ${theme.colors.border}`,
                  background: theme.colors.primaryLight,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                {isImage ? (
                  <img
                    src={dataUrl}
                    alt={att.fileName}
                    css={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : isPdf ? (
                  <a
                    href={dataUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.25rem",
                      flex: 1,
                      padding: "0.5rem",
                      color: theme.colors.primary,
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    <MdOutlinePictureAsPdf size={32} />
                    <span
                      css={{
                        fontSize: "11px",
                        fontFamily: theme.displayFontFamily,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                      title={att.fileName}
                    >
                      {att.fileName}
                    </span>
                  </a>
                ) : (
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.25rem",
                      flex: 1,
                      padding: "0.5rem",
                      color: theme.colors.primary,
                      textAlign: "center",
                    }}
                  >
                    <MdOutlineUploadFile size={32} />
                    <span
                      css={{
                        fontSize: "11px",
                        fontFamily: theme.displayFontFamily,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                      title={att.fileName}
                    >
                      {att.fileName}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAttachment(i);
                  }}
                  aria-label={`Usuń ${att.fileName}`}
                  css={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    width: "22px",
                    height: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: "50%",
                    background: "rgba(0, 0, 0, 0.55)",
                    color: "white",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <MdClose size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
